/// <amd-module name="VSSUI/Components/Checkbox/Checkbox" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext, IFocusZoneContext } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getTabIndex, IFocusable } from "VSSUI/Utilities/Focus";
import { ICheckboxProps, ITriStateCheckboxProps } from "./Checkbox.Props";

let checkboxId = 1;

export class TriStateCheckbox<T extends ITriStateCheckboxProps> extends React.Component<T> implements IFocusable<{}> {
    public static contextType = FocusGroupContext;

    private checkboxElement = React.createRef<HTMLDivElement>();
    private labelId: string;
    private animationClassName: string = "";

    public constructor(props: Readonly<T>) {
        super(props);
        this.labelId = `checkbox-${checkboxId++}-label`;
    }

    public render(): JSX.Element {
        const { disabled, label, labelId, tooltipProps } = this.props;

        return (
            <FocusZoneContext.Consumer>
                {(zoneContext: IFocusZoneContext) => (
                    <Observer checked={this.props.checked}>
                        {(props: { checked: boolean | undefined }) => {
                            let ariaChecked: "false" | "mixed" | "true" | undefined;
                            if (this.props.role !== "presentation") {
                                ariaChecked = props.checked === undefined ? "mixed" : props.checked ? "true" : "false";
                            }
                            let checkbox = (
                                <div
                                    aria-checked={ariaChecked}
                                    aria-describedby={getSafeId(this.props.ariaDescribedBy)}
                                    aria-disabled={this.props.disabled}
                                    aria-label={this.props.ariaLabel}
                                    aria-labelledby={!this.props.ariaLabel ? getSafeId(this.props.ariaLabelledBy) : undefined}
                                    className={css(
                                        this.props.className,
                                        "bolt-checkbox cursor-pointer",
                                        props.checked !== false && "checked",
                                        disabled ? "disabled" : "enabled",
                                        "bolt-focus-treatment",
                                        label && "labelled"
                                    )}
                                    data-focuszone={
                                        !disabled && css(this.props.focuszoneId, !this.props.excludeFocusZone ? zoneContext.focuszoneId : undefined)
                                    }
                                    id={getSafeId(this.props.id)}
                                    onClick={!disabled ? this.onClick : undefined}
                                    onFocus={this.onFocus}
                                    onKeyDown={this.onKeyDown}
                                    ref={this.checkboxElement}
                                    role={this.props.role || "checkbox"}
                                    tabIndex={getTabIndex(this.props, this.context)}
                                >
                                    <div className="bolt-checkmark justify-center flex-row flex-noshrink scroll-hidden">
                                        {Icon({
                                            className: this.animationClassName,
                                            iconName: props.checked === undefined ? "SkypeMinus" : "CheckMark",
                                            size: IconSize.small
                                        })}
                                    </div>
                                    {label && (
                                        <div className="bolt-checkbox-label" id={getSafeId(labelId || this.labelId)}>
                                            {label}
                                        </div>
                                    )}
                                    {this.props.children}
                                </div>
                            );

                            if (tooltipProps) {
                                checkbox = (
                                    <Tooltip addAriaDescribedBy={true} {...tooltipProps}>
                                        {checkbox}
                                    </Tooltip>
                                );
                            }

                            return checkbox;
                        }}
                    </Observer>
                )}
            </FocusZoneContext.Consumer>
        );
    }

    public componentDidMount() {
        this.animationClassName = "animation-ready";
    }

    public focus(): void {
        if (this.checkboxElement.current) {
            this.checkboxElement.current.focus();
        }
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        this.focus();
        this.onChange(event);
    };

    private onFocus = () => {
        const id: string | undefined = this.props.id;
        if (id) {
            this.context.onFocus(id);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented && !this.props.disabled) {
            if (event.which === KeyCode.space) {
                this.onChange(event);
                event.preventDefault();
            }
        }
    };

    private onChange = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (this.props.onChange) {
            let checked = ObservableLike.getValue<boolean | undefined>(this.props.checked);

            // Unchecked transitions to Checked.
            // Checked transitions to TriState or Unchecked.
            // Indeterminate transitions to Unchecked.
            if (checked === false) {
                checked = true;
            } else if (checked === true) {
                if (this.props.triState) {
                    checked = undefined;
                } else {
                    checked = false;
                }
            } else {
                checked = false;
            }

            this.props.onChange(event, checked);
        }
    };
}

export class Checkbox extends TriStateCheckbox<ICheckboxProps> {
    public static defaultProps: ICheckboxProps = {
        checked: false
    };
}
